import {Box, Grid, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useState} from 'react';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../src/assets/images/info-icon.svg';
import './PoolContainer.scss';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import ArrNumericInput from '../../../common/arr-numeric-input/ArrNumericInput';
import {useDispatch, useSelector} from 'react-redux';
import {
  setDrawFromPoolCount,
  setEmailThreadsTogether,
  setIsDrawFromPool,
  setKeepFamiliesTogether,
} from '../../../../redux/slice/assignmentSlice';
import {RootState} from '../../../../redux/store';

const PoolContainer = () => {
  const dispatch = useDispatch();
  const assignmentModel = useSelector(
    (state: RootState) => state.assignment.formData.assignmentModel
  );
  const treeData = useSelector(
    (state: RootState) => state.assignment.assignmentTree
  );
  const isParentCascaseEnabled = treeData?.find(
    (item) => +item.id === assignmentModel.parentAssignmentGroupID
  )?.li_attr.isCascadeEnabled;
  const [error, setError] = useState({docPerDraw: ''});

  const handleDrawFromPoolToggle = useCallback(() => {
    const next = !assignmentModel.isDrawFromPool;
    dispatch(setIsDrawFromPool(next));
    if (!next) {
      dispatch(setKeepFamiliesTogether(false));
      dispatch(setEmailThreadsTogether(false));
      dispatch(setDrawFromPoolCount(0));
      setError({docPerDraw: ''});
    } else {
      const defaultCount = 100;
      dispatch(setKeepFamiliesTogether(true));
      dispatch(setEmailThreadsTogether(true));
      dispatch(setDrawFromPoolCount(defaultCount));
      setError({docPerDraw: ''});
    }
  }, [dispatch, assignmentModel]);

  const handleKeepFamToggle = useCallback(() => {
    const next = !assignmentModel.isKeepFamiliesTogether;
    dispatch(setKeepFamiliesTogether(next));
  }, [dispatch, assignmentModel]);

  const handleKeepEmailToggle = useCallback(() => {
    const next = !assignmentModel.isKeepEmailThreadsTogether;
    dispatch(setEmailThreadsTogether(next));
  }, [dispatch, assignmentModel]);

  const onChangeDocPerDraw = (val: number): void => {
    dispatch(setDrawFromPoolCount(val));
    if (!val && val !== 0) {
      return setError((pre) => ({
        ...pre,
        docPerDraw: 'Draw From Pool Value is required',
      }));
    } else if (val < 20 || val > 2500) {
      return setError((pre) => ({
        ...pre,
        docPerDraw: 'Documents per Draw value should be between 20 and 2500',
      }));
    } else {
      return setError((pre) => ({
        ...pre,
        docPerDraw: '',
      }));
    }
  };

  return (
    <Box className="pool-container">
      <Box className="pool-input-container">
        <Grid container columnSpacing={4} rowSpacing={3}>
          <Grid size={{xs: 12, sm: 6}} className="pool-grid">
            <InputField
              label={t('manageAssignments.poolContainer.drawFromPool')}
              tooltip={t(
                'manageAssignments.poolContainer.tooltip.drawFromPool.body'
              )}
              toggleValue={assignmentModel.isDrawFromPool}
              onChangeToggle={handleDrawFromPoolToggle}
              toggleDisabled={
                isParentCascaseEnabled
              }
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}} className="pool-grid">
            <InputField
              label={t('manageAssignments.poolContainer.documentsPerDraw')}
              tooltip={t(
                'manageAssignments.poolContainer.tooltip.documentsPerDraw'
              )}
              type="text"
              numInputDisabled={
                !assignmentModel.isDrawFromPool ||
                isParentCascaseEnabled
              }
              numValue={assignmentModel.drawFromPoolCount}
              onChangeNum={onChangeDocPerDraw}
              errorMessage={error.docPerDraw}
            />
          </Grid>
        </Grid>
        <Box className="pool-input-sub-container">
          <Grid size={{xs: 12, sm: 6}} className="pool-grid">
            <InputField
              label={t('manageAssignments.poolContainer.keepFamiliesTogether')}
              tooltip={t(
                'manageAssignments.poolContainer.tooltip.keepFamiliesTogether'
              )}
              toggleDisabled={
                !assignmentModel.isDrawFromPool ||
                isParentCascaseEnabled
              }
              toggleValue={assignmentModel.isKeepFamiliesTogether}
              onChangeToggle={handleKeepFamToggle}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}} className="pool-grid">
            <InputField
              label={t(
                'manageAssignments.poolContainer.keepEmailThreadsTogether'
              )}
              tooltip={t(
                'manageAssignments.poolContainer.tooltip.keepEmailThreadsTogether'
              )}
              toggleDisabled={
                !assignmentModel.isDrawFromPool || isParentCascaseEnabled
              }
              toggleValue={assignmentModel.isKeepEmailThreadsTogether}
              onChangeToggle={handleKeepEmailToggle}
            />
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

interface InputFieldProps {
  label?: string;
  type?: string;
  errorMessage?: string;
  numInputDisabled?: boolean;
  numValue?: number | undefined;
  tooltip?: string;
  onChangeNum?: (newValue: number) => void | undefined;
  toggleDisabled?: boolean;
  toggleValue?: boolean;
  onChangeToggle?: () => void | undefined;
}

const InputField = ({
  label,
  type = '',
  numValue,
  tooltip,
  onChangeNum,
  toggleValue,
  onChangeToggle,
  errorMessage = '',
  numInputDisabled = false,
  toggleDisabled = false,
}: InputFieldProps) => {
  const handleNumChange = (e: number | undefined) => {
    if (!Number.isNaN(e)) {
      onChangeNum?.(e ?? 0);
    }
  };
  return (
    <>
      <Box className="title-box input-field-container">
        <Typography
          className="typo-text label1_size title"
          data-testid={`${label}-title`}
        >
          {label}
          {type === 'text' && '*'}&nbsp;
        </Typography>
        <ArrTooltipWrapper
          title={label}
          trigger={
            <InfoIcon
              className="info-icon label1_size"
              data-testid={`${label}-title-info`}
            />
          }
        >
          {tooltip}
        </ArrTooltipWrapper>
      </Box>
      {type === 'text' ? (
        <ArrNumericInput
          // min={20}
          // max={2500}
          sizeVariant="medium"
          dataTestId="doc-per-draw-input"
          showPercentage={false}
          placeholder={`${numValue === undefined ? 'Count' : '0'}`}
          value={numValue}
          onChange={handleNumChange}
          // state={errorMessage ? 'error' : 'enabled'}
          isError={errorMessage ? true : false}
          disabled={numInputDisabled}
          supportText={errorMessage}
        />
      ) : (
        <ArrToggle
          dataTestId={`${label}-toggle`}
          value={toggleValue}
          onChange={onChangeToggle}
          toggleSize="small"
          disabled={toggleDisabled}
        />
      )}
    </>
  );
};

export default PoolContainer;
